import style from './type-curve-selector.module.scss';
import { SingleSelectForm } from '../../single select form/single-select-form';
import { Toggle } from 'components/toggle/toggle';
import { useEffect, useMemo, useState } from 'react';
import { useAtom, useSetAtom } from 'jotai';
import {
  typeCurveOptionsAtom,
  selectedTypeCurveAtom,
  clearDcaNotificationGroupAtom,
  setDcaNotificationAtom,
} from '../atoms';
import { type TypeCurveOption } from 'types/early-time-dca';
import { Link } from '@carbon/react';
import { TypeCurveVerificationStatusIndicator } from 'components/type-curve-verification-status/type-curve-verification-status';
import { TypeCurveStatusTag } from 'components/lifecycle-status-tag';

interface TypeCurveSelectorProps {
  uwi: string;
}

export const TypeCurveSelector = ({ uwi }: TypeCurveSelectorProps) => {
  // Off = show active Type Curves; on = show active + archived
  const [showArchived, setShowArchived] = useState(false);
  const [selectedTypeCurve, setSelectedTypeCurve] = useAtom(
    selectedTypeCurveAtom
  );

  const [typeCurveOptions, fetchTypeCurveHeaders] =
    useAtom(typeCurveOptionsAtom);
  const clearDcaNotifications = useSetAtom(clearDcaNotificationGroupAtom);
  const setDcaNotifications = useSetAtom(setDcaNotificationAtom);

  useEffect(() => {
    setSelectedTypeCurve(null);
    fetchTypeCurveHeaders({
      type: 'POST',
      url: '/queries/wells/type_curve_headers',
      data: {
        bulkTypeCurveHeaders: [
          // `showArchived: false` = show active Type Curves; `true` = show active + archived
          { uwi, active: showArchived ? undefined : true },
        ],
      },
    });
  }, [uwi, showArchived, fetchTypeCurveHeaders, setSelectedTypeCurve]);

  const options = useMemo(() => {
    if (typeCurveOptions.result && typeCurveOptions.result.type === 'success') {
      const totalOptions = typeCurveOptions.result.data.results[uwi];
      if (!totalOptions || !Array.isArray(totalOptions)) return [];
      return totalOptions.map((option) => ({
        ...option,
        id: option.uniqueTcId,
      }));
    }
    return [];
  }, [typeCurveOptions.result, uwi]);

  useEffect(() => {
    if (!selectedTypeCurve && options.length > 0) {
      setDcaNotifications('type-curve-selection', [
        {
          id: 'no-type-curve-selected',
          title: 'Early Time DCA',
          message:
            'Cannot match a Type Curve, please select one in the dropdown',
          type: 'warning',
        },
      ]);
      clearDcaNotifications('type-curves');
    } else {
      clearDcaNotifications('type-curve-selection');
    }
  }, [selectedTypeCurve, setDcaNotifications, clearDcaNotifications, options]);

  useEffect(() => {
    if (options.length > 0) {
      const preselectedOption = options.find((option) => option.isPreselected);
      if (preselectedOption) {
        setSelectedTypeCurve(preselectedOption);
      } else {
        setSelectedTypeCurve(null);
      }
    }
  }, [options, setSelectedTypeCurve]);

  return (
    <SingleSelectForm
      id="type-curve-selection"
      options={options}
      onChange={(value) => {
        setSelectedTypeCurve(value);
      }}
      className={style.typeCurveSelectorContainer}
      value={selectedTypeCurve}
      showNoneOption={true}
      isLoading={typeCurveOptions.loading}
      onSearch={(text, option) =>
        option.name.toLowerCase().includes(text.toLowerCase())
      }
      renderLabel={(option) => (
        <TypeCurveOption
          name={option.name}
          uniqueTcId={option.uniqueTcId}
          status={option.status}
          promoted={option.promoted}
          archived={option.archived}
          isDefault={option.isDefault}
          verificationStatus={option.verificationStatus}
          lastVerifiedBy={option.lastVerifiedBy}
          href={option.href}
          formation={option.formation}
          horizontalWellSpacingFt={option.wellSpacingFt}
        />
      )}
      header={
        <div className={style.typeCurveActiveToggleContainer}>
          <Toggle
            isActive={showArchived}
            handleToggle={() => setShowArchived(!showArchived)}
            label="Archived"
          />
        </div>
      }
    />
  );
};

interface TypeCurveOptionProps {
  name: string;
  uniqueTcId: string;
  status: string;
  promoted?: boolean;
  archived?: boolean;
  isDefault?: boolean;
  verificationStatus?: string | null;
  lastVerifiedBy?: string | null;
  href: string;
  formation: string;
  horizontalWellSpacingFt: string;
}

const TypeCurveOption = ({
  uniqueTcId,
  status,
  promoted,
  archived,
  isDefault,
  verificationStatus,
  lastVerifiedBy,
  href,
  formation,
  horizontalWellSpacingFt,
}: TypeCurveOptionProps) => {
  return (
    <div className={style.optionContent}>
      <div className={style.optionHeader}>
        <TypeCurveVerificationStatusIndicator
          status={verificationStatus}
          showLabel={false}
          hoverVerifiedBy
          lastVerifiedBy={lastVerifiedBy}
          className={style.verificationStatusIcon}
          iconClasses={{
            correct: style.verificationStatusCorrect,
            incorrect: style.verificationStatusIncorrect,
          }}
        />
        <Link className={style.optionName} target="_blank" href={href}>
          {uniqueTcId}
        </Link>
        <TypeCurveStatusTag
          promoted={promoted}
          isDefault={isDefault}
          archived={archived}
          className={style.statusTag}
        />
      </div>
      <div className={style.optionDetails}>
        {formation} | {horizontalWellSpacingFt} | {status}
      </div>
    </div>
  );
};
